import { playerRepository, teamRepository } from "../../storage/storage";
import SummaryContextFactory from "./summary-context-factory";
import {
    getFieldDirectionMultiplier,
    getYardsToFirstDownFriendly
} from "../../sports/football/utils/context-utils";

class FootballStringifier {
    firstInitial(player) {
        if (Array.isArray(player)) {
            return player
                .map((p) => {
                    return this.firstInitial(p);
                })
                .join(", ");
        }
        if (player?.id) {
            let hydrated;
            try {
                hydrated = playerRepository.getFromCacheSync(player.id);
            } catch (err) {
                return "Not Found";
            }
            if (hydrated.firstName && hydrated.lastName) {
                return `${hydrated.firstName.substring(0, 1)}. ${
                    hydrated.lastName
                }`;
            }
            if (hydrated.lastName) {
                return `${hydrated.lastName}`;
            }
            if (hydrated.uniform) {
                return `#${hydrated.uniform}`;
            }
        }

        return "Unknown";
    }

    niceFieldPosition(fieldLength, fieldPosition, home = "H", visit = "V") {
        const endZoneYardline =
            fieldPosition > fieldLength || fieldPosition < 0;

        if (fieldPosition < fieldLength / 2) {
            return `${home?.toUpperCase() || "H"}${
                endZoneYardline ? " " : ""
            }${fieldPosition}`;
        } else {
            return `${visit?.toUpperCase() || "V"}${
                endZoneYardline ? " " : ""
            }${fieldLength - fieldPosition}`;
        }
    }

    gain(summaryContext, start, end, tdAllowed = true) {
        const multi = getFieldDirectionMultiplier(summaryContext);
        const diff = (end - start) * multi;
        if (diff === 0) {
            return "no gain";
        }
        if (
            !summaryContext.triggerPAT &&
            !summaryContext.isPatPlay &&
            ((tdAllowed && multi < 0 && end <= 0) ||
                (tdAllowed &&
                    multi > 0 &&
                    end >= summaryContext.gameConfig.general.fieldLength))
        ) {
            summaryContext.eopPrinted = true;
            return `a ${diff}-yard touchdown`;
        }
        if (diff === 1 || diff === -1) {
            return `${diff} yard`;
        }
        return `${diff} yards`;
    }

    ordinalSuffix(down) {
        var j = down % 10,
            k = down % 100;
        if (j === 1 && k !== 11) {
            return down + "st";
        }
        if (j === 2 && k !== 12) {
            return down + "nd";
        }
        if (j === 3 && k !== 13) {
            return down + "rd";
        }
        return down + "th";
    }

    secondsToMMSS(seconds) {
        const d = Number(seconds);

        const m = Math.floor(d / 60);
        const s = Math.floor(d % 60);

        return ("0" + m).slice(-2) + ":" + ("0" + s).slice(-2);
    }

    secondsToHHMMSS(seconds) {
        const d = Number(seconds);

        const h = Math.floor(d / 3600);
        const m = Math.floor((d % 3600) / 60);
        const s = Math.floor((d % 3600) % 60);

        return (
            ("0" + h).slice(-2) +
            ":" +
            ("0" + m).slice(-2) +
            ":" +
            ("0" + s).slice(-2)
        );
    }

    milisecondsToMMSS(miliseconds) {
        const d = Number(miliseconds);
        return this.secondsToMMSS(d / 1000);
    }

    milisecondsToHHMMSS(miliseconds) {
        const d = Number(miliseconds);
        return this.secondsToHHMMSS(d / 1000);
    }

    generateDownAndPosition(play, gameContext) {
        const contextSnapshot = play.getContextSnapshot();
        const { homeTeamId, awayTeamId } = gameContext;
        const homeAbbrName =
            teamRepository.getFromCacheSync(homeTeamId)?.abbrName;
        const awayAbbrName =
            teamRepository.getFromCacheSync(awayTeamId)?.abbrName;
        const down = this.ordinalSuffix(contextSnapshot.down);
        const yards = getYardsToFirstDownFriendly(
            contextSnapshot,
            gameContext.gameConfig
        );
        const fieldPosition = this.niceFieldPosition(
            gameContext.gameConfig.general.fieldLength,
            contextSnapshot.fieldPosition,
            homeAbbrName,
            awayAbbrName
        );

        return `${down} and ${yards} at ${fieldPosition}`;
    }

    generateSummary(play, gameContext) {
        const playFragments = play.fragments || [];
        const contextSnapshot = play.getContextSnapshot();
        const summaryContext = new SummaryContextFactory().build(
            contextSnapshot,
            gameContext
        );

        const fragments = playFragments.reduce(
            (acc, f) => {
                const { title, message, details } =
                    f.summarize(acc.summaryContext, this, play) || {};
                f.updateSummaryContext(
                    acc.summaryContext,
                    play,
                    gameContext.gameConfig
                );

                if (title) acc.title = `${acc.title} ${title}`;
                if (message && message.length > 0) acc.messages.push(message);
                if (details && details.length > 0) acc.details.push(...details);

                return acc;
            },
            {
                title: "",
                messages: [],
                details: [],
                summaryContext
            }
        );

        if (!fragments.summaryContext.sopPrinted) {
            fragments.title = `${this.generateDownAndPosition(
                play,
                gameContext
            )}:`;
        }

        if (fragments.messages.length) {
            if (!fragments.summaryContext.eopPrinted) {
                fragments.messages.push(
                    `to ${this.niceFieldPosition(
                        gameContext.gameConfig.general.fieldLength,
                        fragments.summaryContext.fieldPosition,
                        summaryContext.homeTeam.abbrName,
                        summaryContext.awayTeam.abbrName
                    )}`
                );
            }
        }

        return fragments;
    }

    summarize(play, gameContext) {
        return this.generateSummary(play, gameContext);
    }
}

export default new FootballStringifier();
